import type { CustomProduct } from "@apptus/esales-api";
import isMatch from "lodash.ismatch";
import type { VariantState } from "types/variant";

export type VariantAction =
  | {
      type: "SELECT";
      key: string;
      value: string | number | boolean;
    }
  | {
      type: "LIGHTSOURCE";
      value?: CustomProduct;
    }
  | {
      type: "INIT";
      state: VariantState;
    };

export default function reducer(state: VariantState, action: VariantAction) {
  switch (action.type) {
    case "SELECT": {
      /** Selected attributes before new attributes are adjusted */
      const oldAttrs = { ...state.selection } as VariantState["selection"];

      /** Index of the new changed attribute */
      const index = state.attributes.findIndex((a) => a.key === action.key);

      /** Attributes in order up until the changed attribute */
      const attrs = state.attributes.slice(0, index);

      /** Current set of attributes without the changed value */
      const curAttrs = Object.fromEntries(
        attrs.map(({ key }) => [key, state.selection[key]]),
      ) as VariantState["selection"];

      /** New set of attributes with the changed value */
      const newAttrs = {
        ...curAttrs,
        [action.key]: action.value,
      } as VariantState["selection"];

      /**
       * If all attributes are set we should ideally only have one variant left to choose from
       * (otherwise we have a data issue and should log something)
       */
      if (Object.keys(newAttrs).length === state.attributes.length) {
        const [variant] = state.variants.filter((v) => isMatch(v, newAttrs));

        if (!variant) {
          console.error("Missing variant combination");
        }

        return {
          ...state,
          selection: newAttrs,
          variant,
        };
      }

      /**
       * Count matches for the current attribute combination
       */
      const variantMatches = state.variants.filter((v) => isMatch(v, newAttrs));

      if (variantMatches.length === 1 && variantMatches[0]) {
        const [variant] = variantMatches;
        const selection = Object.fromEntries([
          ...state.attributes.map(({ key }) => [key, variant[key]]),
        ]) as VariantState["selection"];
        return {
          ...state,
          selection,
          variant,
        };
      }

      /**
       * Double check if a variant has a combination of old attributes and changed attribute
       */
      const fallbackMatches = state.variants.filter((v) =>
        isMatch(v, { ...oldAttrs, [action.key]: action.value }),
      );

      if (fallbackMatches.length === 1 && fallbackMatches[0]) {
        const [variant] = fallbackMatches;
        const selection = Object.fromEntries([
          ...state.attributes.map(({ key }) => [key, variant[key]]),
        ]) as VariantState["selection"];
        return {
          ...state,
          selection,
          variant,
        };
      }

      /**
       * Otherwise just reset the variant and set the new selection
       */
      return {
        ...state,
        selection: newAttrs,
        variant: undefined,
      };
    }
    case "LIGHTSOURCE": {
      return { ...state, lightsource: action.value };
    }
    case "INIT": {
      return { ...state, ...action.state };
    }
    default: {
      return state;
    }
    // case 'SET_VARIANT': {
    //   if (!action.payload) {
    //     return state;
    //   }

    //   if (state?.variant?.id === action.payload.id) return state;

    //   const attrs = JSON.parse(action.payload.VariantDefiningAttributes);
    //   const keys: string[] = Object.keys(attrs);

    //   const selection: Record<string, string | number> = keys.reduce(
    //     (a, b) => ({ ...a, [b]: action.payload?.[b] }),
    //     {},
    //   );

    //   return {
    //     ...state,
    //     selection,
    //     variant: action.payload,
    //     options: generateOptions(state.attributes, selection, state.order),
    //   };
    // }
    // default: {
    //   throw new Error('VariantContext - Unhandled action type');
    // }
  }
}
